import React, { useEffect, useState } from "react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { UserCircle, Plus, Loader2, FileSpreadsheet } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import api, { bulkUploadDonors } from "@/lib/api";
import BulkUploadModal from "@/components/BulkUploadModal";

const get = (url) => api.get(url).then((r) => r.data);
const post = (url, body) => api.post(url, body).then((r) => r.data);
function money(n) { return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function Donors() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", notes: "", tags: "" });
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const load = () => get("/api/donors").then(setRows);
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      setBusy(true);
      await post("/api/donors", { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast({ title: "Donor added" });
      setOpen(false); setForm({ full_name: "", email: "", phone: "", notes: "", tags: "" });
      load();
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail, variant: "destructive" }); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader title="Donors" subtitle="The community of people who chose to trust this organization with their gifts.">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowBulkModal(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card text-foreground px-3.5 py-2 text-sm font-medium hover:bg-accent transition-colors"
          >
            <FileSpreadsheet size={14} className="text-primary" /> Bulk upload
          </button>
          <button onClick={() => setOpen(true)} data-testid="donors-new" className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm font-medium">
            <Plus size={14} /> Add donor
          </button>
        </div>
      </PageHeader>
      <BulkUploadModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        entityType="donors"
        onUpload={bulkUploadDonors}
        onSuccess={load}
      />
      <PageBody>
        {open && (
          <div className="rounded-xl border border-border bg-card p-5 mb-5 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <input placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="px-3 py-2 rounded border border-border bg-background" data-testid="donors-name" />
              <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-3 py-2 rounded border border-border bg-background" />
              <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="px-3 py-2 rounded border border-border bg-background" />
              <input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="px-3 py-2 rounded border border-border bg-background" />
              <textarea placeholder="Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="px-3 py-2 rounded border border-border bg-background col-span-2" />
            </div>
            <div className="flex gap-2">
              <button onClick={submit} disabled={busy || !form.full_name} data-testid="donors-submit" className="inline-flex items-center gap-1 px-3 py-2 rounded bg-primary text-primary-foreground text-sm">
                {busy && <Loader2 size={14} className="animate-spin" />} Save donor
              </button>
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded border border-border text-sm">Cancel</button>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground bg-accent/30">
              <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Tags</th><th className="text-right px-4 py-3">Total given</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-muted-foreground"><UserCircle size={28} className="mx-auto mb-2 opacity-30" /> No donors yet.</td></tr>}
              {rows.map((d) => (
                <tr key={d.id} className="border-t border-border/50" data-testid={`donor-${d.id}`}>
                  <td className="px-4 py-2.5 font-medium">{d.full_name}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{d.email || "—"}</td>
                  <td className="px-4 py-2.5">{(d.tags || []).map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 mr-1 rounded bg-primary/10 text-primary">{t}</span>)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{money(d.total_given)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PageBody>
    </>
  );
}
